import { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api";
import { createPortal } from "react-dom";
import { MoreVertical, Coins, Pencil, Trash2 } from "lucide-react";
import AddCreditModal from "../../../components/AddCreditModal";

export default function Student() {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [showCreditModal, setShowCreditModal] = useState(false);

    const handleMenuToggle = (event, studentId) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + window.scrollY,
            left: rect.right + window.scrollX - 160,
        });
        setMenuOpen(menuOpen === studentId ? null : studentId);
    };

    const addCredit = (studentId) => {
        setMenuOpen(null); // close menu
        setShowCreditModal(studentId); // pass student id
    };

    // Find the selected student to pass to the handlers
    const selectedStudent = students.find((s) => s.id === menuOpen);

    useEffect(() => {
        loadStudents();
    }, []);

    const loadStudents = async () => {
        try {
            setLoading(true);
            const res = await api.fetchStudents();
            setStudents(res.data);
        } catch (err) {
            setError("Failed to load students: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (studentId) => {
        if (!window.confirm("Are you sure you want to delete this student?")) return;
        try {
            await api.deleteStudent(studentId);
            loadStudents();
        } catch (err) {
            alert("Failed to delete student: " + err.message);
        }
    };

    const handleEdit = (student) => {
        setMenuOpen(null);
        navigate("/student-edit/", { state: student });
    };

    return (
        <DashboardLayout>
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h1 className="text-xl sm:text-2xl font-bold">Students</h1>
                <Link to="/student-new" className="w-full sm:w-auto">
                    <button className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium" data-label="New Student">
                        + New Student
                    </button>
                </Link>
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block bg-white shadow rounded-lg overflow-x-auto">
                <div className="p-4">
                    {loading ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600">Loading students...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-8">
                            <p className="text-red-500">{error}</p>
                        </div>
                    ) : students.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600">No students found.</p>
                        </div>
                    ) : (
                        <div>
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="text-gray-600 border-b">
                                        <th className="py-3 px-4 text-sm font-semibold">ID</th>
                                        <th className="py-3 px-4 text-sm font-semibold">Name</th>
                                        <th className="py-3 px-4 text-sm font-semibold">Email</th>
                                        <th className="py-3 px-4 text-sm font-semibold">Class</th>
                                        <th className="py-3 px-4 text-sm font-semibold">Status</th>
                                        <th className="py-3 px-4 text-sm font-semibold text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {students.map((s) => (
                                        <tr key={s.id} className="border-b hover:bg-gray-50 transition-colors">
                                            <td className="py-3 px-4">{s.id}</td>
                                            <td className="py-3 px-4">{s.name}</td>
                                            <td className="py-3 px-4">{s.email}</td>
                                            <td className="py-3 px-4">{s.class_name}</td>
                                            <td className="py-3 px-4">
                                                <span className={`font-medium ${s.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                                    {s.is_active ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-center relative">
                                                <button
                                                    className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                                                    onClick={(e) => handleMenuToggle(e, s.id)}
                                                    data-label="Student Options"
                                                    title="Actions"
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            
                            {/* The portal for the credit transfer popup */}
                            {showCreditModal && (
                                <AddCreditModal
                                    recpID={showCreditModal}
                                    type='st'
                                    labelName='Student'
                                    onClose={() => setShowCreditModal(false)}
                                    onSuccess={loadStudents}
                                />
                            )}
                            
                            {/* The portal for the context menu */}
                            {menuOpen &&
                                selectedStudent &&
                                createPortal(
                                    <div
                                        className="absolute bg-white border shadow-lg rounded z-50 min-w-[160px]"
                                        style={{ top: menuPosition.top, left: menuPosition.left }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors"
                                            onClick={() => handleEdit(selectedStudent)}
                                            data-label="Edit Student"
                                        >
                                            <Pencil size={14} /> Edit
                                        </button>
                                        <button
                                            className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-gray-100 transition-colors"
                                            onClick={() => addCredit(selectedStudent.id)}
                                            data-label="Add Credit Student"
                                        >
                                            <Coins size={14} /> Add Credit
                                        </button>
                                        <button
                                            className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-red-100 text-red-600 transition-colors"
                                            onClick={() => handleDelete(selectedStudent.id)}
                                            data-label="Delete Student"
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>,
                                    document.body
                                )}
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
                {loading ? (
                    <div className="bg-white shadow rounded-lg p-6 text-center">
                        <p className="text-gray-600">Loading students...</p>
                    </div>
                ) : error ? (
                    <div className="bg-white shadow rounded-lg p-6 text-center">
                        <p className="text-red-500">{error}</p>
                    </div>
                ) : students.length === 0 ? (
                    <div className="bg-white shadow rounded-lg p-6 text-center">
                        <p className="text-gray-600">No students found.</p>
                    </div>
                ) : (
                    students.map((s) => (
                        <div
                            key={s.id}
                            className="bg-white shadow rounded-lg p-4 border border-gray-200"
                        >
                            <div className="space-y-3">
                                <div className="flex items-center justify-between border-b pb-2">
                                    <span className="text-xs text-gray-500 font-medium">ID</span>
                                    <span className="text-sm font-semibold text-gray-900">{s.id}</span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-medium">Name</span>
                                    <span className="text-sm text-gray-900 font-medium">{s.name}</span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-medium">Email</span>
                                    <span className="text-sm text-gray-900 break-all">{s.email}</span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-medium">Class</span>
                                    <span className="text-sm text-gray-900">{s.class_name}</span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 font-medium">Status</span>
                                    <span className={`text-sm font-medium ${s.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                        {s.is_active ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                
                                <div className="pt-2 border-t space-y-2">
                                    <button
                                        onClick={() => handleEdit(s)}
                                        className="flex items-center justify-center gap-1.5 w-full text-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                        data-label="Edit Student"
                                    >
                                        <Pencil size={14} /> Edit
                                    </button>
                                    <button
                                        onClick={() => addCredit(s.id)}
                                        className="flex items-center justify-center gap-1.5 w-full text-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                                        data-label="Add Credit Student"
                                    >
                                        <Coins size={14} /> Add Credit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(s.id)}
                                        className="flex items-center justify-center gap-1.5 w-full text-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors font-medium"
                                        data-label="Delete Student"
                                    >
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* The portal for the credit transfer popup */}
            {showCreditModal && (
                <AddCreditModal
                    recpID={showCreditModal}
                    type='st'
                    labelName='Student'
                    onClose={() => setShowCreditModal(false)}
                    onSuccess={loadStudents}
                />
            )}

            {/* Click outside to close menu */}
            {menuOpen && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setMenuOpen(null)}
                />
            )}
        </DashboardLayout>
    );
}
